import { useEffect, useState } from "react";
import type { FormatPrefs } from "../lib/i18n.ts";
import { formatTime as fmtTimeI18n, formatDate as fmtDateI18n } from "../lib/i18n.ts";
import { getServices, getStaff, updateAppointment } from "../lib/api.ts";
import { decodeEntities } from "../lib/decode-entities.ts";

export interface DetailsAppt {
  id: string;
  start_at: string;
  end_at: string;
  status: string;
  customer_name: string;
  customer_email?: string | null;
  customer_phone?: string | null;
  service_name: string;
  /** JSON array string from the API; first id = primary service. */
  service_ids?: string;
  staff_id: string;
  staff_name: string;
  notes?: string | null;
}

interface ServiceOpt {
  id: string;
  name: string;
  duration_min: number;
  is_active: number;
}

interface StaffOpt {
  id: string;
  name: string;
  is_active: number;
}

interface Props {
  appt: DetailsAppt;
  slug: string;
  onClose: () => void;
  /** (id, status) → status update (legacy quick-action path). */
  onAction: (id: string, status: string) => Promise<void> | void;
  /** Refetch parent's appointment list after a successful edit. */
  onUpdated?: () => void;
  formatPrefs?: FormatPrefs;
  /** Opens the Collect-payment modal for this appointment. */
  onCollectPayment?: () => void;
}

function fmtTime(iso: string, prefs?: FormatPrefs): string {
  if (prefs) return fmtTimeI18n(iso, prefs);
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function fmtDate(iso: string, prefs?: FormatPrefs): string {
  if (prefs)
    return fmtDateI18n(iso, prefs, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function statusStyle(status: string): { bg: string; text: string; label: string } {
  const map: Record<string, { bg: string; text: string; label: string }> = {
    confirmed: { bg: "var(--status-confirmed-bg)", text: "var(--status-confirmed)", label: "Confirmed" },
    tentative: { bg: "var(--status-tentative-bg)", text: "var(--status-tentative)", label: "Tentative" },
    completed: { bg: "var(--status-done-bg)", text: "var(--status-done)", label: "Complete" },
    no_show: { bg: "var(--status-noshow-bg)", text: "var(--status-noshow)", label: "No-show" },
    cancelled: { bg: "var(--status-cancelled-bg)", text: "var(--status-cancelled)", label: "Cancelled" },
  };
  return map[status] ?? { bg: "var(--paper-well)", text: "var(--ink-soft)", label: status };
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 12, padding: "10px 0", borderBottom: "1px solid var(--rule)" }}>
      <div style={{ flex: "0 0 96px", fontSize: 12.5, color: "var(--ink-faint)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 500, paddingTop: 8 }}>
        {label}
      </div>
      <div style={{ flex: 1, fontSize: 15, color: "var(--ink)", minWidth: 0, wordBreak: "break-word" }}>
        {children}
      </div>
    </div>
  );
}

/** Convert UTC ISO → local "YYYY-MM-DD" + "HH:MM" parts (browser-local). */
function isoToLocalParts(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return { date: `${y}-${m}-${dd}`, time: `${hh}:${mi}` };
}

/** Local date "YYYY-MM-DD" + time "HH:MM" → UTC ISO string. */
function localPartsToISO(date: string, time: string): string {
  return new Date(`${date}T${time}:00`).toISOString();
}

export function AppointmentDetailsModal({ appt, slug, onClose, onAction, onUpdated, formatPrefs, onCollectPayment }: Props) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [services, setServices] = useState<ServiceOpt[]>([]);
  const [staffList, setStaffList] = useState<StaffOpt[]>([]);

  // Edit form state — initialized from the appointment.
  const initialServiceId = (() => {
    try {
      const ids = JSON.parse(appt.service_ids ?? "[]");
      return Array.isArray(ids) && typeof ids[0] === "string" ? ids[0] : "";
    } catch {
      return "";
    }
  })();
  const initialParts = isoToLocalParts(appt.start_at);
  const [serviceId, setServiceId] = useState(initialServiceId);
  const [staffId, setStaffId] = useState(appt.staff_id);
  const [date, setDate] = useState(initialParts.date);
  const [time, setTime] = useState(initialParts.time);
  const [notes, setNotes] = useState(appt.notes ?? "");
  const [status, setStatus] = useState(appt.status);

  // Lazy-load services/staff only when entering edit mode.
  useEffect(() => {
    if (!editing) return;
    if (services.length > 0 && staffList.length > 0) return;
    let cancelled = false;
    Promise.all([getServices(slug), getStaff(slug)])
      .then(([svc, stf]) => {
        if (cancelled) return;
        const sList: ServiceOpt[] = Array.isArray(svc) ? (svc as ServiceOpt[]) : [];
        const tList: StaffOpt[] = Array.isArray(stf) ? (stf as StaffOpt[]) : [];
        setServices(sList.filter((s) => s.is_active));
        setStaffList(tList.filter((s) => s.is_active));
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [editing, slug, services.length, staffList.length]);

  const cur = statusStyle(status);

  const act = async (next: string) => {
    setBusy(next);
    try {
      await onAction(appt.id, next);
      onClose();
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    setBusy("save");
    setError(null);
    try {
      const startISO = localPartsToISO(date, time);
      const patch: Record<string, unknown> = {
        service_ids: [serviceId],
        staff_id: staffId,
        start_at: startISO,
        notes: notes.trim() || null,
      };
      // Server recomputes end_at from the new service's duration when
      // service_ids changes. Only send status if it differs from the
      // current value (otherwise the lifecycle messaging fires on no-op).
      if (status !== appt.status) patch.status = status;
      await updateAppointment(slug, appt.id, patch);
      onUpdated?.();
      setEditing(false);
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  // Open = still actionable (operator can complete / no-show / cancel).
  // Tentative bookings are still open — the operator may want to firm
  // them up by editing or close them out.
  const isOpen = appt.status === "confirmed" || appt.status === "tentative";

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center md:justify-center">
      <div
        className="absolute inset-0"
        style={{ background: "rgba(0,0,0,0.55)" }}
        onClick={busy ? undefined : onClose}
      />

      <div
        className="relative w-full md:max-w-md rounded-t-2xl md:rounded-2xl max-h-[92vh] overflow-y-auto"
        style={{
          background: "var(--card-bg)",
          border: "1px solid var(--card-border)",
        }}
      >
        {/* Grab handle (mobile) */}
        <div className="flex justify-center pt-3 pb-2 md:hidden">
          <div className="w-10 h-1 rounded-full" style={{ background: "var(--border)" }} />
        </div>

        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-3"
          style={{ borderBottom: "1px solid var(--border)" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h2 style={{ fontSize: "1.15rem", fontWeight: 600, color: "var(--text)" }}>
              {editing ? "Edit appointment" : "Appointment"}
            </h2>
            {!editing && (
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: cur.bg,
                  color: cur.text,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                }}
              >
                {cur.label}
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {!editing && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="btn btn-ghost"
                style={{ fontSize: 14 }}
                title="Edit"
              >
                Edit
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              disabled={!!busy}
              className="text-lg"
              style={{ color: "var(--text-muted)" }}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>

        {error && (
          <div
            className="mx-5 mt-3"
            style={{
              padding: "8px 10px",
              borderRadius: 8,
              background: "var(--status-cancelled-bg)",
              color: "var(--status-cancelled)",
              fontSize: 14,
            }}
          >
            {error}
          </div>
        )}

        {/* Body */}
        <div className="px-5 py-2">
          {!editing ? (
            // ─── Read-only view ─────────────────────────────────────────
            <>
              <Row label="Customer">
                <div style={{ fontWeight: 600 }}>{decodeEntities(appt.customer_name)}</div>
                {(appt.customer_phone || appt.customer_email) && (
                  <div style={{ marginTop: 4, display: "flex", flexDirection: "column", gap: 2, fontSize: 14 }}>
                    {appt.customer_phone && (
                      <a href={`tel:${appt.customer_phone}`} style={{ color: "var(--accent-ink)", textDecoration: "none" }}>
                        {appt.customer_phone}
                      </a>
                    )}
                    {appt.customer_email && (
                      <a href={`mailto:${appt.customer_email}`} style={{ color: "var(--accent-ink)", textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {appt.customer_email}
                      </a>
                    )}
                  </div>
                )}
              </Row>

              <Row label="Service">{decodeEntities(appt.service_name)}</Row>

              <Row label="When">
                <div>{fmtDate(appt.start_at, formatPrefs)}</div>
                <div style={{ fontVariantNumeric: "tabular-nums", fontSize: 14, color: "var(--ink-soft)", marginTop: 2 }}>
                  {fmtTime(appt.start_at, formatPrefs)} – {fmtTime(appt.end_at, formatPrefs)}
                </div>
              </Row>

              <Row label="Staff">{appt.staff_name}</Row>

              {appt.notes && (
                <Row label="Notes">
                  <div style={{ whiteSpace: "pre-wrap", fontSize: 14.5 }}>{appt.notes}</div>
                </Row>
              )}
            </>
          ) : (
            // ─── Edit view ──────────────────────────────────────────────
            <>
              <Row label="Customer">
                <div style={{ fontWeight: 600 }}>{decodeEntities(appt.customer_name)}</div>
                <div style={{ fontSize: 13, color: "var(--ink-faint)", marginTop: 2 }}>
                  Edit customer details from the Customers page.
                </div>
              </Row>

              <Row label="Service">
                <select
                  className="input"
                  value={serviceId}
                  onChange={(e) => setServiceId(e.target.value)}
                >
                  {services.length === 0 && <option value={initialServiceId}>{decodeEntities(appt.service_name)}</option>}
                  {services.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} · {s.duration_min} min
                    </option>
                  ))}
                </select>
              </Row>

              <Row label="When">
                <div className="flex gap-2">
                  <input
                    type="date"
                    className="input"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <input
                    type="time"
                    className="input"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    style={{ flex: 1 }}
                  />
                </div>
                <div style={{ fontSize: 11, color: "var(--ink-faint)", marginTop: 4 }}>
                  Duration auto-derived from the chosen service.
                </div>
              </Row>

              <Row label="Staff">
                <select className="input" value={staffId} onChange={(e) => setStaffId(e.target.value)}>
                  {staffList.length === 0 && <option value={appt.staff_id}>{appt.staff_name}</option>}
                  {staffList.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </Row>

              <Row label="Status">
                <select
                  className="input"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="confirmed">Confirmed</option>
                  <option value="tentative">Tentative</option>
                  <option value="completed">Complete</option>
                  <option value="no_show">No-show</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </Row>

              <Row label="Notes">
                <textarea
                  rows={3}
                  className="input resize-none"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Internal notes about this appointment"
                />
              </Row>
            </>
          )}
        </div>

        {/* Footer: status-change actions (open appointments only) */}
        {!editing && isOpen && (
          <div
            className="px-5 py-3 flex flex-wrap gap-2"
            style={{ borderTop: "1px solid var(--border)" }}
          >
            <button
              type="button"
              disabled={!!busy}
              onClick={() => act("completed")}
              className="btn-primary"
              style={{ flex: "1 1 120px" }}
            >
              {busy === "completed" ? "..." : "Mark complete"}
            </button>
            <button
              type="button"
              disabled={!!busy}
              onClick={() => act("no_show")}
              className="btn-secondary"
              style={{ flex: "1 1 120px" }}
            >
              {busy === "no_show" ? "..." : "No-show"}
            </button>
            <button
              type="button"
              disabled={!!busy}
              onClick={() => act("cancelled")}
              className="btn-secondary"
              style={{ flex: "1 1 120px", color: "var(--status-cancelled)" }}
            >
              {busy === "cancelled" ? "..." : "Cancel booking"}
            </button>
          </div>
        )}
        {/* Footer: Checkout — available at any appointment status */}
        {!editing && onCollectPayment && (
          <div
            className="px-5 py-3"
            style={{ borderTop: "1px solid var(--border)" }}
          >
            <button
              type="button"
              className="btn-primary"
              style={{ width: "100%" }}
              onClick={onCollectPayment}
            >
              Checkout
            </button>
          </div>
        )}
        {editing && (
          <div
            className="px-5 py-3 flex flex-wrap gap-2 justify-end"
            style={{ borderTop: "1px solid var(--border)" }}
          >
            <button
              type="button"
              disabled={!!busy}
              onClick={() => setEditing(false)}
              className="btn-secondary"
              style={{ fontSize: 14 }}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!!busy}
              onClick={save}
              className="btn-primary"
              style={{ fontSize: 14 }}
            >
              {busy === "save" ? "Saving…" : "Save changes"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
